import React from "react";
import { useParams, Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import Navbar from "../NavBar";
import Footer from "../Footer";
import Capabilities from "../Capabilities";
import Industries from "../industries";
import Insights from "../Insights";
import AboutUs from "../AboutUs";
import Search1 from "../Search";
import ContactBanner from "../ContactBanner";
import content from "../../content/content.json";

export default function CaseStudyPage() {
  const { id } = useParams();
  const { caseStudies } = content;
  const study = caseStudies.find((s) => String(s.id) === String(id));
  const project = content.projectsPage?.projects?.find((p) => String(p.id) === String(id));

  const { scrollYProgress } = useScroll();
  const xPos = useTransform(scrollYProgress, [0, 1], ["60vw", "-60vw"]);
  const yPos = useTransform(scrollYProgress, [0, 1], ["-60vh", "60vh"]);
  const carouselImages = study ? getCaseStudyCarouselImages(study, project) : [];

  return (
    <section className="w-full relative">
      <Navbar
        capabilities={<Capabilities />}
        industries={<Industries />}
        insights={<Insights />}
        about={<AboutUs />}
        search={<Search1 />}
      />

      {!study ? (
        <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-[#edf3f7]">
          <h2 className="text-3xl font-bold text-gray-800">Case Study Not Found</h2>
          <Link to="/projects" className="text-[#E4002B] font-semibold underline">
            ← Back to Case Studies
          </Link>
        </div>
      ) : (
        <>
          {/* Hero — split layout like About */}
          <div className="relative w-full min-h-[500px] flex flex-col md:flex-row">
            <div className="w-full md:w-1/2 bg-[#1C1F2B] flex items-center px-6 md:px-20 py-16">
              <div className="text-white max-w-xl">
                <Link
                  to="/projects"
                  className="text-xs text-gray-400 hover:text-white mb-6 flex items-center gap-1 transition-colors"
                >
                  ← All Case Studies
                </Link>
                <span className="text-xs font-bold text-[#007681] uppercase tracking-widest mb-4 block">
                  {study.heroTag}
                </span>
                <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-4">
                  {study.heroTitle}
                </h1>
                <p className="text-white/70 text-sm leading-relaxed mb-5">{study.heroSubtitle}</p>
                <p className="text-[#E4002B] font-semibold text-sm">{study.heroStat}</p>
              </div>
            </div>
            <div className="w-full md:w-1/2 relative h-64 md:h-auto">
              <img
                src={study.heroImage}
                alt={study.heroTitle}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[#1C1F2B]/50 to-[#1C1F2B]" />
            </div>
          </div>

          {/* CTA strip */}
          <div className="bg-white px-6 md:px-20 py-5 flex flex-wrap gap-4 border-b border-gray-200 shadow-sm">
            <a
              href="#"
              className="flex items-center gap-2 border border-[#E4002B] text-[#E4002B] px-6 py-3 text-sm font-semibold hover:bg-[#E4002B] hover:text-white transition-colors duration-200"
            >
              ↓ {study.ctaDownload}
            </a>
            <Link
              to="/contact"
              className="flex items-center gap-2 bg-[#1C1F2B] text-white px-6 py-3 text-sm font-semibold hover:bg-[#E4002B] transition-colors duration-200"
            >
              → {study.ctaContact}
            </Link>
          </div>

          {/* Executive Summary — red left accent like About */}
          <section className="bg-white text-gray-800 py-16 px-6 lg:px-20 relative">
            <div className="absolute left-0 lg:left-20 top-10 w-[6px] h-44 bg-red-600" />
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl lg:text-4xl font-bold mb-8">
                {study.executiveSummary.title}
              </h2>
              {study.executiveSummary.paragraphs.map((p, i) => (
                <p key={i} className="max-w-4xl text-gray-600 leading-relaxed mb-4 text-base">
                  {p}
                </p>
              ))}
            </div>
          </section>

          {/* Selected Screens Carousel */}
          {study.selectedScreens && (
            <section className="bg-[#f5f7fa] py-14 px-6 lg:px-20 border-t border-gray-100">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-2xl font-bold mb-1 text-gray-800">
                  {study.selectedScreens.title}
                </h2>
                <p className="text-gray-400 text-xs italic mb-6">
                  {study.selectedScreens.caption}
                </p>
                {carouselImages.length > 0 ? (
                  <EnhancedScreenCarousel images={carouselImages} />
                ) : (
                  <div className="w-full h-52 bg-gray-200 border border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-sm rounded">
                    Screen images coming soon
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Two-column: System Snapshot + Jump Links — like About story section */}
          <section className="bg-white text-gray-800 py-12 px-6 lg:px-20 border-t border-gray-100">
            <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-start justify-between gap-12">
              <div className="lg:w-3/5">
                <h2 className="text-2xl font-bold mb-8">{study.snapshot.title}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {study.snapshot.items.map((item, i) => (
                    <div key={i} className="border-l-4 border-[#007681] pl-4 py-2">
                      <p className="text-[10px] font-bold text-[#007681] uppercase tracking-widest">
                        {item.label}
                      </p>
                      {item.label === "System Access" ? (
                        <div className="mt-1 space-y-1">
                          {item.liveUrl ? (
                            <a
                              href={item.liveUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-sm font-semibold text-[#E4002B] hover:underline"
                            >
                              Live system →
                            </a>
                          ) : (
                            <span className="block text-sm text-gray-400">Live system →</span>
                          )}
                          <span className="block text-xs text-gray-400">or</span>
                          {item.archivedUrl ? (
                            <a
                              href={item.archivedUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-sm font-semibold text-[#007681] hover:underline"
                            >
                              Archived System Snapshot (Wayback) →
                            </a>
                          ) : (
                            <span className="block text-sm text-gray-400">
                              Archived System Snapshot (Wayback) →
                            </span>
                          )}
                        </div>
                      ) : (
                        <p className="text-gray-800 font-semibold mt-1 text-sm">{item.value}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:w-1/3">
                <h3 className="text-xl font-bold mb-5">Jump to Section</h3>
                <ol className="space-y-3 list-decimal pl-5">
                  {study.jumpLinks.map((link, i) => (
                    <li key={i}>
                      <a href={`#${link.anchor}`} className="text-red-600 hover:underline font-medium">
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </section>

          {/* Colored sections — like About's scrolling blocks with floating image */}
          <div className="relative">
            {study.sections.map((section, idx) => (
              <div
                key={section.id}
                id={section.id}
                className="min-h-[420px] flex items-center px-6 md:px-20 py-16 relative overflow-hidden"
                style={{ backgroundColor: section.bg }}
              >
                {/* Big watermark text */}
                <div className="absolute inset-0 pointer-events-none select-none">
                  <h1 className="text-[100px] md:text-[180px] font-bold text-white/10 leading-none absolute top-0 left-0">
                    {section.bgTextTop}
                  </h1>
                  <h1 className="text-[100px] md:text-[180px] font-bold text-white/10 leading-none absolute bottom-0 right-0">
                    {section.bgTextBottom}
                  </h1>
                </div>

                <div className="max-w-2xl text-white z-10">
                  <h3 className="text-2xl md:text-3xl font-bold mb-4">{section.title}</h3>
                  {section.intro && (
                    <p className="text-white/80 mb-5 text-sm leading-relaxed">{section.intro}</p>
                  )}
                  {section.items && section.items.length > 0 && (
                    <ul className="space-y-2 mb-4">
                      {section.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-white/90 text-sm">
                          <span className="text-[#E4002B] mt-0.5 flex-shrink-0">▸</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.body && (
                    <p className="text-white/60 mt-4 italic text-sm">{section.body}</p>
                  )}
                </div>
              </div>
            ))}

            {/* Floating scroll-animated panel — like About's floating images */}
            {study.sections.map((section, idx) => (
              <FloatingPanel
                key={idx}
                section={section}
                idx={idx}
                total={study.sections.length}
                scrollYProgress={scrollYProgress}
                xPos={xPos}
                yPos={yPos}
              />
            ))}
          </div>

          {/* Outcomes */}
          <section id="outcomes" className="bg-[#edf3f7] py-20 px-6 md:px-20">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl lg:text-4xl font-bold mb-12 text-gray-800">
                {study.outcomes.title}
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <ul className="space-y-4">
                    {study.outcomes.stats.map((stat, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="text-[#E4002B] font-bold text-lg mt-0.5 flex-shrink-0">✓</span>
                        <span className="text-gray-700 font-medium">{stat}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-6 text-gray-800">Before → After</h3>
                  <div className="space-y-4">
                    {study.outcomes.beforeAfter.map((row, i) => (
                      <div key={i} className="flex items-center gap-4">
                        <span className="w-36 text-gray-400 line-through text-sm">{row.before}</span>
                        <span className="text-[#E4002B] font-bold">→</span>
                        <span className="text-gray-800 font-semibold text-sm">{row.after}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-8 text-gray-500 italic text-sm">{study.outcomes.summary}</p>
                </div>
              </div>
            </div>
          </section>

          {/* What This Proves + Trust Signals */}
          <section className="bg-white py-16 px-6 md:px-20">
            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
              <div>
                <h2 className="text-2xl font-bold mb-3 text-gray-800">
                  {study.whatThisProves.title}
                </h2>
                <p className="text-gray-500 mb-5 text-sm">{study.whatThisProves.intro}</p>
                <ul className="space-y-3">
                  {study.whatThisProves.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-700 text-sm">
                      <span className="text-[#007681] mt-0.5 flex-shrink-0">▸</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-5 text-gray-800">
                  {study.trustSignals.title}
                </h2>
                <ul className="space-y-3">
                  {study.trustSignals.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-700 text-sm">
                      <span className="text-[#E4002B] mt-0.5 flex-shrink-0">▸</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Closing quote — dark panel */}
          <section className="bg-[#1C1F2B] py-20 px-6 md:px-20 text-center">
            <p className="text-white text-xl md:text-2xl font-light italic max-w-3xl mx-auto leading-relaxed">
              "{study.closing}"
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="bg-[#E4002B] text-white px-8 py-4 font-semibold text-sm hover:bg-red-700 transition-colors duration-200"
              >
                {study.ctaContact} →
              </Link>
              <Link
                to="/projects"
                className="border border-white text-white px-8 py-4 font-semibold text-sm hover:bg-white hover:text-[#1C1F2B] transition-colors duration-200"
              >
                ← All Case Studies
              </Link>
            </div>
          </section>

          <ContactBanner />
        </>
      )}

      <Footer />
    </section>
  );
}

const fallbackScreenImages = [
  "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1400&q=80",
  "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1400&q=80",
  "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1400&q=80",
];

function getCaseStudyCarouselImages(study, project) {
  const screenImages = Array.isArray(study.selectedScreens?.images)
    ? study.selectedScreens.images
    : [];
  const imageCandidates = [
    ...screenImages,
    study.heroImage,
    project?.image,
    ...fallbackScreenImages,
  ];

  const uniqueImages = imageCandidates.reduce((images, image) => {
    if (!image) return images;

    const imageKey = image.split("?")[0];
    const alreadyAdded = images.some((existingImage) => existingImage.split("?")[0] === imageKey);

    return alreadyAdded ? images : [...images, image];
  }, []);

  return uniqueImages.slice(0, 3);
}

function EnhancedScreenCarousel({ images }) {
  const [current, setCurrent] = React.useState(0);
  const [expandedImage, setExpandedImage] = React.useState(null);

  React.useEffect(() => {
    if (!expandedImage) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setExpandedImage(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [expandedImage]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setExpandedImage(images[current])}
        className="block w-full cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-[#007681] focus:ring-offset-2"
        aria-label={`Open screen ${current + 1} full size`}
      >
        <img
          src={images[current]}
          alt={`Screen ${current + 1}`}
          className="h-[120px] w-full object-cover bg-gray-100 rounded shadow"
        />
      </button>
      {images.length > 1 && (
        <div className="flex items-center justify-center gap-4 mt-4">
          <button
            type="button"
            onClick={() => setCurrent((c) => (c - 1 + images.length) % images.length)}
            className="w-10 h-10 inline-flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-sm font-semibold rounded transition-colors"
            aria-label="Previous screen"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="text-sm text-gray-500">
            {current + 1} / {images.length}
          </span>
          <button
            type="button"
            onClick={() => setCurrent((c) => (c + 1) % images.length)}
            className="w-10 h-10 inline-flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-sm font-semibold rounded transition-colors"
            aria-label="Next screen"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}

      {expandedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 p-4 md:p-8 flex items-center justify-center"
          onClick={() => setExpandedImage(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Full screen image preview"
        >
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setExpandedImage(null);
            }}
            className="absolute right-4 top-4 w-11 h-11 inline-flex items-center justify-center rounded-full bg-white text-gray-900 hover:bg-gray-200 transition-colors"
            aria-label="Close image preview"
          >
            <X size={22} />
          </button>
          <img
            src={expandedImage}
            alt={`Screen ${current + 1} full size`}
            className="max-w-full max-h-full object-contain rounded bg-white"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

function ScreenCarousel({ images }) {
  const [current, setCurrent] = React.useState(0);
  return (
    <div className="relative">
      <img
        src={images[current]}
        alt={`Screen ${current + 1}`}
        className="h-[80px] w-full object-cover bg-gray-100 rounded shadow"
      />
      {images.length > 1 && (
        <div className="flex items-center justify-center gap-4 mt-4">
          <button
            onClick={() => setCurrent((c) => (c - 1 + images.length) % images.length)}
            className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-sm font-semibold rounded transition-colors"
          >
            ←
          </button>
          <span className="text-sm text-gray-500">
            {current + 1} / {images.length}
          </span>
          <button
            onClick={() => setCurrent((c) => (c + 1) % images.length)}
            className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-sm font-semibold rounded transition-colors"
          >
            →
          </button>
        </div>
      )}
    </div>
  );
}

function FloatingPanel({ section, idx, total, scrollYProgress, xPos, yPos }) {
  const start = idx / total;
  const end = (idx + 1) / total;

  const opacity = useTransform(
    scrollYProgress,
    [start, start + 0.08, end - 0.08, end],
    [0, 1, 1, 0]
  );

  return (
    <motion.div
      className="w-[260px] h-[260px] fixed top-1/2 left-1/2 z-20 pointer-events-none rounded-lg shadow-2xl overflow-hidden flex flex-col items-center justify-center p-6 text-center"
      style={{
        backgroundColor: section.bg,
        border: "1px solid rgba(255,255,255,0.15)",
        x: xPos,
        y: yPos,
        opacity,
        marginTop: "-130px",
        marginLeft: "-130px",
      }}
    >
      <p className="text-white/50 text-[10px] uppercase tracking-widest mb-2">Section</p>
      <p className="text-white font-bold text-xl leading-tight">{section.title}</p>
      {section.items && (
        <p className="text-white/60 text-xs mt-3">{section.items.length} key points</p>
      )}
    </motion.div>
  );
}
